/**
 * IMPORTS
 */
import {t} from 'i18next';
import accounts from 'src/aggregates/accounts/selectors';
import conversations from  'src/aggregates/conversations/selectors';
import customers from 'src/aggregates/customers/selectors';
import {contactTypesChannelToId} from 'src/aggregates/customers/utils';
import {getConversation as getTimeline}
    from 'src/aggregates/timelines/selectors';
import {showPopup} from 'src/aggregates/user/api';
import {fetch as fetchUser} from 'src/aggregates/user/selectors';
import {logger} from 'src/logger';


/**
 * CONSTANT AND DEFINITIONS
 */
// popup types
const ERROR = 'error';
const WARNING = 'warning';


/**
 * CODE
 */

/**
 * I show a contact already exists popup.
 *
 * :param channel: contact channel
 * :param id: customer id
 * :param name: customer name
 * :param uid: contact uid
 *
 * :returns: nothing
 */
async function showContactAlreadyExistsPopup (
    channel: string,
    id: string | number,
    name: string,
    uid: string,
): Promise<void>
{
    // get the contact, for its display name
    const held = customers.getContactByChannelAndUid(
        contactTypesChannelToId.get(channel),
        uid,
    );

    await showPopup(
        ERROR,
        t(
            '{{ channel }} {{ contact }} já está associado ao cliente {{ id }} - {{ name }}',
            {
                replace: {
                    channel,
                    contact: held?.displayName ??
                        uid?.replace('@c.us', '') ?? '',
                    id,
                    name,
                },
            },
        ),
        t('Cliente já cadastrado'),
    );
}


/**
 * I show a message fail popup.
 *
 * :param error: error name
 * :param data: error data
 * :param conversationId: conversation Id
 *
 * :returns: nothing
 */
async function showMessageFailurePopup(
    error : string,
    data : any,
    conversationId: number,
    messageId: number,
): Promise<void>
{
    // get user id and company
    const {id, company} = fetchUser();

    // search for conversation on conversations or timelines
    const conversation =
        conversations.get(conversationId) ??
        getTimeline(conversationId);

    // conversation not found: log and abort
    if (conversation === null)
    {
        logger.error('CVJPZ0053E', {
            company,
            id,
            reason: 'Conversation not found',
        });
        return;
    }

    // get the conversation account
    const account = accounts.get(conversation.account);

    // get conversation channel
    const channel = account.channel;

    // get the contact of the conversation
    const contact = customers.getContactByChannelAndUid(
        contactTypesChannelToId.get(channel?.toLowerCase()),
        conversation.remote,
    );

    // set displayable contact, the raw number when the contact is unknown
    const displayable = contact?.displayName ??
        conversation.remote.replace('@c.us', '');

    // show Popup to client
    await showPopup(
        ERROR,
        t(error, {replace: {channel, contact: displayable}}),
        t('Falha no envio da mensagem'),
    );
}


/**
 * I show a number mismatch popup.
 *
 * :param contact: contact uid
 *
 * :returns: nothing
 */
async function showNumberMismatchPopup (contact: string): Promise<void>
{
    await showPopup(
        WARNING,
        t(
            'Foi adicionado a este cliente o contato {{ contact }} que é utilizado pelo WhatsApp.',
            {
                replace: {
                    contact: contact.replace('@c.us', ''),
                },
            },
        ),
        t('Mensagem padrão enviada com sucesso'),
    );
}


/**
 * I show a conversation unavailable popup.
 *
 * :returns: nothing
 */
async function showUnavailableConversationPopup (): Promise<void>
{
    await showPopup(
        WARNING,
        t('Esta conversa não está mais em atendimento por você.'),
        t('Conversa indisponível'),
    );
}


/**
 * EXPORTS
 */
export
{
    showContactAlreadyExistsPopup,
    showMessageFailurePopup,
    showNumberMismatchPopup,
    showUnavailableConversationPopup,
};
